import ExcelJS from 'exceljs'
import {
  STAFF_COLUMNS,
  colIndexOf,
  colLetter,
  type StaffTemplateContext,
} from './columns'

const INPUT_ROWS = 200 // rows 2..201 carry dropdowns, helper formulas & band checks
const BRAND = 'FFF26F21'

function midpoint(min: number | null, max: number | null, fixed: number | null): number | null {
  if (fixed !== null && fixed > 0) return fixed
  if (min !== null && max !== null) return Math.round((min + max) / 2)
  if (min !== null) return min
  return null
}

/**
 * Build a per-tenant Staff onboarding workbook:
 *  - "Staff" sheet with locked dropdowns (Title / Schedule / Location / Gender /
 *    Status / NHF), grade + band-min/max helper columns (VLOOKUP against the
 *    hidden "Lists" sheet), and a Basic-salary cell bounded by that band.
 *  - a hidden "Lists" sheet holding the tenant's real lookup data.
 *  - one pre-filled EXAMPLE row.
 */
export async function generateStaffTemplate(
  ctx: StaffTemplateContext,
  shopName: string,
): Promise<Blob> {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Palstro'
  wb.created = new Date(0) // deterministic; avoids Date.now in build/test envs

  // ── Lists (hidden) ──────────────────────────────────────────────────────
  const lists = wb.addWorksheet('Lists')
  lists.state = 'hidden'
  lists.getCell('A1').value = 'Schedules'
  ctx.schedules.forEach((s, i) => { lists.getCell(`A${i + 2}`).value = s.name })
  lists.getCell('C1').value = 'Title'
  lists.getCell('D1').value = 'Grade'
  ctx.titles.forEach((t, i) => {
    lists.getCell(`C${i + 2}`).value = t.title_name
    lists.getCell(`D${i + 2}`).value = t.grade_name
  })
  lists.getCell('F1').value = 'Grade'
  lists.getCell('G1').value = 'Min'
  lists.getCell('H1').value = 'Max'
  ctx.grades.forEach((g, i) => {
    const min = g.grade_type === 'fixed' ? (g.fixed ?? 0) : (g.min ?? 0)
    const max = g.grade_type === 'fixed' ? (g.fixed ?? 0) : (g.max ?? 0)
    lists.getCell(`F${i + 2}`).value = g.name
    lists.getCell(`G${i + 2}`).value = min
    lists.getCell(`H${i + 2}`).value = max
  })
  lists.getCell('J1').value = 'Location'
  ctx.locations.forEach((l, i) => { lists.getCell(`J${i + 2}`).value = l.name })
  lists.getCell('L1').value = 'Gender'
  ;['Male', 'Female', 'Other'].forEach((v, i) => { lists.getCell(`L${i + 2}`).value = v })
  lists.getCell('M1').value = 'Status'
  ;['active', 'suspended', 'terminated', 'retired', 'resigned'].forEach((v, i) => { lists.getCell(`M${i + 2}`).value = v })
  lists.getCell('N1').value = 'YesNo'
  ;['Yes', 'No'].forEach((v, i) => { lists.getCell(`N${i + 2}`).value = v })

  const scheduleRange = `Lists!$A$2:$A$${ctx.schedules.length + 1}`
  const titleRange = `Lists!$C$2:$C$${ctx.titles.length + 1}`
  const locationRange = `Lists!$J$2:$J$${ctx.locations.length + 1}`
  const genderRange = 'Lists!$L$2:$L$4'
  const statusRange = 'Lists!$M$2:$M$6'
  const yesNoRange = 'Lists!$N$2:$N$3'

  // ── Staff (data entry) ──────────────────────────────────────────────────
  const ws = wb.addWorksheet('Staff', { views: [{ state: 'frozen', ySplit: 1 }] })
  ws.columns = STAFF_COLUMNS.map(c => ({
    header: c.header + (c.required ? ' *' : ''),
    key: c.key,
    width: c.width ?? 16,
  }))

  const header = ws.getRow(1)
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND } }
  header.alignment = { vertical: 'middle' }
  header.height = 22

  // Column letters for the wired-up columns.
  const cTitle = colLetter(colIndexOf('title'))
  const cGrade = colLetter(colIndexOf('gradeHelper'))
  const cMin = colLetter(colIndexOf('minHelper'))
  const cMax = colLetter(colIndexOf('maxHelper'))
  const cBasic = colLetter(colIndexOf('basic'))
  const cSchedule = colLetter(colIndexOf('schedule'))
  const cLocation = colLetter(colIndexOf('location'))
  const cGender = colLetter(colIndexOf('gender'))
  const cStatus = colLetter(colIndexOf('status'))
  const cNhf = colLetter(colIndexOf('yesno'))
  const cDob = colLetter(STAFF_COLUMNS.findIndex(c => c.key === 'date_of_birth') + 1)
  const cDoe = colLetter(STAFF_COLUMNS.findIndex(c => c.key === 'date_of_employment') + 1)

  // Grey styling for the read-only helper columns.
  for (const c of [cGrade, cMin, cMax]) {
    ws.getColumn(c).eachCell?.({ includeEmpty: true }, () => { /* set per-row below */ })
  }

  const firstRow = 2
  const lastRow = INPUT_ROWS + 1

  for (let r = firstRow; r <= lastRow; r++) {
    // Helper formulas: grade from title, band from grade.
    ws.getCell(`${cGrade}${r}`).value = { formula: `IFERROR(VLOOKUP($${cTitle}${r},Lists!$C:$D,2,FALSE),"")`, result: '' }
    ws.getCell(`${cMin}${r}`).value = { formula: `IFERROR(VLOOKUP($${cGrade}${r},Lists!$F:$H,2,FALSE),"")`, result: '' }
    ws.getCell(`${cMax}${r}`).value = { formula: `IFERROR(VLOOKUP($${cGrade}${r},Lists!$F:$H,3,FALSE),"")`, result: '' }
    for (const c of [cGrade, cMin, cMax]) {
      ws.getCell(`${c}${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F3F5' } }
      ws.getCell(`${c}${r}`).font = { color: { argb: 'FF6B7280' } }
    }
    ws.getCell(`${cMin}${r}`).numFmt = '#,##0'
    ws.getCell(`${cMax}${r}`).numFmt = '#,##0'
    ws.getCell(`${cBasic}${r}`).numFmt = '#,##0'

    // Dropdowns.
    ws.getCell(`${cTitle}${r}`).dataValidation = listDV(titleRange, 'Pick a job title', 'Choose a job title from the list — staff can only use your set-up titles.')
    ws.getCell(`${cSchedule}${r}`).dataValidation = listDV(scheduleRange, 'Pick a schedule', 'Choose one of your working patterns.')
    ws.getCell(`${cLocation}${r}`).dataValidation = listDV(locationRange, 'Pick a location', 'Choose one of your locations.')
    ws.getCell(`${cGender}${r}`).dataValidation = listDV(genderRange, 'Gender', 'Male, Female or Other.')
    ws.getCell(`${cStatus}${r}`).dataValidation = listDV(statusRange, 'Employment status', 'Leave blank for active.')
    ws.getCell(`${cNhf}${r}`).dataValidation = listDV(yesNoRange, 'NHF enrolled', 'Yes or No.')

    // Basic salary bounded by the grade band shown in the helper columns.
    ws.getCell(`${cBasic}${r}`).dataValidation = {
      type: 'decimal',
      operator: 'between',
      allowBlank: true,
      formulae: [`$${cMin}${r}`, `$${cMax}${r}`],
      showInputMessage: true,
      promptTitle: 'Basic salary (annual)',
      prompt: 'Enter an annual basic within the grade band shown in “Band min/Band max”. The server rejects anything outside it.',
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: 'Outside the grade band',
      error: 'Basic salary must be between the grade’s Band min and Band max.',
    }

    // Dates: format + validation.
    for (const c of [cDob, cDoe]) {
      ws.getCell(`${c}${r}`).numFmt = 'yyyy-mm-dd'
      ws.getCell(`${c}${r}`).dataValidation = {
        type: 'date',
        operator: 'between',
        allowBlank: true,
        formulae: [new Date(Date.UTC(1900, 0, 1)), new Date(Date.UTC(2100, 0, 1))],
        showErrorMessage: true,
        errorTitle: 'Invalid date',
        error: 'Use a real date (format YYYY-MM-DD).',
      }
    }
  }

  // ── Sample (EXAMPLE) row ────────────────────────────────────────────────
  const sampleTitle = ctx.titles[0]
  const sampleSchedule = ctx.schedules[0]
  const sampleLocation = ctx.locations[0]
  const sampleGrade = sampleTitle ? ctx.grades.find(g => g.id === sampleTitle.grade_id) ?? null : null
  const sampleBasic = sampleGrade ? midpoint(sampleGrade.min, sampleGrade.max, sampleGrade.fixed) : null

  const sample = ws.getRow(2)
  sample.getCell('first_name').value = 'EXAMPLE Amara'
  sample.getCell('last_name').value = 'Okafor'
  sample.getCell('gender').value = 'Female'
  sample.getCell('date_of_birth').value = '1992-04-15'
  sample.getCell('phone').value = '08030000000'
  sample.getCell('email').value = 'amara@example.com'
  sample.getCell('date_of_employment').value = '2023-01-09'
  sample.getCell('employment_status').value = 'active'
  sample.getCell('department').value = 'Operations'
  if (sampleLocation) sample.getCell('location_name').value = sampleLocation.name
  if (sampleTitle) sample.getCell('job_title').value = sampleTitle.title_name
  if (sampleSchedule) sample.getCell('schedule_name').value = sampleSchedule.name
  if (sampleBasic !== null) sample.getCell('basic_salary').value = sampleBasic
  sample.getCell('nhf_enrolled').value = 'No'
  sample.font = { italic: true, color: { argb: 'FF94A3B8' } }

  // ── Instructions sheet ──────────────────────────────────────────────────
  const info = wb.addWorksheet('Instructions')
  const infoRows: string[][] = [
    [`${shopName} — Staff upload template`],
    [''],
    ['1. Fill one row per employee on the "Staff" sheet. The first row is a greyed EXAMPLE — delete it or leave it (it is skipped on upload).'],
    ['2. Job title, Working schedule and Location are dropdowns — only your set-up values are allowed.'],
    ['3. Picking a Job title auto-fills Grade, Band min and Band max (the grey columns). Do not type in those.'],
    ['4. Basic salary is the ANNUAL basic and must fall inside the grade band shown. The server re-checks every row.'],
    ['5. Dates use YYYY-MM-DD. Required columns are marked with " *".'],
    ['6. Leave Personnel # blank to auto-number, or set your own unique numbers.'],
    [''],
    ['When done, save as .xlsx and upload it back on the Staff page.'],
  ]
  infoRows.forEach((r, i) => { info.getCell(`A${i + 1}`).value = r[0] })
  info.getColumn('A').width = 120
  info.getRow(1).font = { bold: true, size: 14 }

  const buf = await wb.xlsx.writeBuffer()
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

function listDV(range: string, title: string, prompt: string): ExcelJS.DataValidation {
  return {
    type: 'list',
    allowBlank: true,
    formulae: [range],
    showInputMessage: true,
    promptTitle: title,
    prompt,
    showErrorMessage: true,
    errorStyle: 'error',
    errorTitle: 'Not in the list',
    error: 'Please choose a value from the dropdown.',
  }
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
